import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import TakingsSummaryCard from "./components/TakingsSummaryCard";
import DailyTroncTable from "./components/DailyTroncTable";
import HistoricalComparison from "./components/HistoricalComparison";
import ManualEntryPanel from "./components/ManualEntryPanel";
import Pagination from "../../components/ui/Pagination";
import Icon from "../../components/AppIcon";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import { useTroncRecords } from "../../hooks/useTroncRecords";
import { useTenantContext } from "../../contexts/TenantContext";
import { useLocationContext } from "../../contexts/LocationContext";
import { troncRecordApi } from "../../services/api/troncRecords";
import { transactionsApi } from "../../services/api/transactions";

// Standard page size for all list views
const DEFAULT_PAGE_SIZE = 25;

const DailyTakingsAndTronc = () => {
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [notification, setNotification] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [historicalLoading, setHistoricalLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Date filtering state - default to last 7 days
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
  return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const [dateFilter, setDateFilter] = useState(getDefaultDateRange());

  // Tenant and location context
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();
  const { currentLocation, locations, hasLocations } = useLocationContext();

  // Only fetch data when tenant is selected (or for non-global admins)
  const shouldFetchData = !isGlobalAdmin || selectedTenant;

  // Fetch real data
  const {
    records: troncRecords,
    loading,
    error,
    refetch,
  } = useTroncRecords({
    autoFetch: shouldFetchData,
  });

  // Fetch historical comparison data from API
  const fetchHistoricalData = async () => {
    if (!shouldFetchData) return;

    setHistoricalLoading(true);
    try {
      const params = {};
      if (currentLocation?.id) {
    params.locationId = currentLocation.id;
    }
   if (selectedTenant?.id) {
        params.tenantId = selectedTenant.id;
      }

      const response = await troncRecordApi.getHistorical(params);

      if (response.success && response.data) {
        setHistoricalData(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch historical data:", err);
    } finally {
      setHistoricalLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoricalData();
  }, [shouldFetchData, currentLocation?.id, selectedTenant?.id]);

  // Filter and sort records
  const filteredRecords = useMemo(() => {
    return (troncRecords || []).filter((r) => {
      const recordDate = new Date(r.periodStart);
      const startDate = new Date(dateFilter.startDate);
      const endDate = new Date(dateFilter.endDate);
    endDate.setHours(23, 59, 59, 999);
    
      // Apply location filter if selected
    const matchesLocation = !currentLocation || r.locationId === currentLocation.id;
 
      return recordDate >= startDate && recordDate <= endDate && matchesLocation;
    });
  }, [troncRecords, dateFilter, currentLocation]);

  // Sort records by date descending (newest first)
  const sortedRecords = useMemo(() => {
    return [...filteredRecords].sort((a, b) => 
      new Date(b.periodStart) - new Date(a.periodStart)
    );
  }, [filteredRecords]);

  // Paginated records
  const paginatedRecords = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
return sortedRecords.slice(startIndex, startIndex + pagination.pageSize);
  }, [sortedRecords, pagination.page, pagination.pageSize]);

  const totalPages = Math.ceil(sortedRecords.length / pagination.pageSize);

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination({ page: 1, pageSize: newPageSize });
  };

  // Reset pagination when filters change
  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Calculate totals - split by cash and card
  const cashTips = filteredRecords.reduce(
    (sum, r) => sum + (r.cashTips || 0),
    0,
  );
  const cardTips = filteredRecords.reduce(
    (sum, r) => sum + (r.cardTips || 0),
    0,
  );
  const serviceCharge = filteredRecords.reduce(
    (sum, r) => sum + (r.serviceCharge || 0),
    0,
  );
  const totalTronc = filteredRecords.reduce(
    (sum, r) => sum + (r.amount || 0),
    0,
);

  // Calculate distribution status
  const distributedCount = filteredRecords.filter(
    r => r.isDistributed || r.status === "Distributed" || r.status === 3
  ).length;
  const pendingCount = filteredRecords.filter(
    r => !r.isDistributed && (r.status === "Draft" || r.status === "Pending" || r.status === "Approved" || r.status === 0 || r.status === 1 || r.status === 2)
  ).length;

  // Prepare historical comparison data from API response
  const preparedHistoricalData = useMemo(() => {
    if (!historicalData) {
      return {
week: { current: 0, previous: 0, peakDay: 0, average: 0, change: 0, changePercent: 0 },
        month: { current: 0, previous: 0, average: 0, change: 0, changePercent: 0 },
      };
    }

    return {
  week: {
        current: historicalData.week?.current || 0,
        previous: historicalData.week?.previous || 0,
        peakDay: historicalData.week?.peak || 0,
        peakDayName: historicalData.week?.peakDay || null,
        average: historicalData.week?.average || 0,
     change: historicalData.week?.change || 0,
        changePercent: historicalData.week?.changePercent || 0,
      },
      month: {
        current: historicalData.month?.current || 0,
        previous: historicalData.month?.previous || 0,
        peak: historicalData.month?.peak || 0,
        peakDayName: historicalData.month?.peakDay || null,
        average: historicalData.month?.average || 0,
  change: historicalData.month?.change || 0,
        changePercent: historicalData.month?.changePercent || 0,
  },
 };
  }, [historicalData]);

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Daily Takings & TRONC", path: "/daily-takings-and-tronc" },
  ];

  const handleRefresh = async () => {
    await refetch();
    await fetchHistoricalData();
    const now = new Date();
    setLastSyncTime(
      now.toLocaleString("en-GB", {
        day: "2-digit",
      month: "2-digit",
    year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    );
    setNotification({
      type: "success",
      message: "Data refreshed successfully",
    });
    setTimeout(() => setNotification(null), 3000);
  };

  // CSV Export helper - comprehensive with proper formatting
  const exportToCsv = (records, filename) => {
    const headers = [
    "Date",
      "Location",
      "Cash Tips",
      "Card Tips",
      "Service Charge",
   "Total TRONC",
      "Status",
      "Notes",
    ];

    const rows = records.map((r) => [
      new Date(r.periodStart).toLocaleDateString("en-GB"),
  r.locationName || "Unknown",
      r.cashTips || 0,
      r.cardTips || 0,
      r.serviceCharge || 0,
      r.amount || 0,
      r.isDistributed ? "Distributed" : r.status || "Pending",
    r.notes || "",
    ]);

    // Properly escape CSV values - handle quotes, commas, newlines
    const escapeCell = (cell) => {
   const str = (cell ?? "").toString();
      if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
     return `"${str.replace(/"/g, '""')}"`;
  }
      return str;
    };

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCell).join(","))
    .join("\r\n");

    // Add BOM for Excel compatibility with special characters
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCurrentPage = () => {
    if (paginatedRecords.length === 0) return;
    exportToCsv(paginatedRecords, `tronc-records-page-${pagination.page}`);
  setNotification({
      type: "success",
      message: `Exported ${paginatedRecords.length} records from current page`,
    });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleExportAll = () => {
    if (sortedRecords.length === 0) return;
    exportToCsv(sortedRecords, `tronc-records-all-${sortedRecords.length}`);
    setNotification({
      type: "success",
      message: `Exported ${sortedRecords.length} records`,
    });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleManualEntry = async (entry) => {
    try {
      if (!currentLocation?.id) {
        setNotification({
          type: "error",
      message: "Please select a location before adding manual entries",
      });
        return;
      }

      // Parse the selected date
      const entryDate = new Date(entry.date);
      const periodStart = new Date(
        entryDate.getFullYear(),
        entryDate.getMonth(),
        entryDate.getDate(),
        0,
      0,
        0,
      );

      // Create transaction record - backend will handle TRONC record update via UpdateTroncRecordForDate
      const transactionPayload = {
        amount: entry.cashTips,
    transactionDate: periodStart.toISOString(),
        locationId: currentLocation.id,
 paymentType: 0, // Cash = 0
  transactionType: 0, // TroncPayment = 0
      description: `Manual cash tips entry`,
        notes: entry.notes || `Manual cash tips: �${entry.cashTips.toFixed(2)}`,
      };

      const transactionResponse = await transactionsApi.create(transactionPayload);

      if (!transactionResponse.success) {
        setNotification({
        type: "error",
    message: transactionResponse.message || "Failed to create transaction record",
        });
        setTimeout(() => setNotification(null), 5000);
        return;
      }

      // Refetch to get updated TRONC records (backend already updated them)
      await refetch();
      await fetchHistoricalData();
      
      // Show success notification
      showSuccessNotification(entry.cashTips, entry.date);
    } catch (error) {
      console.error("Manual entry error:", error);
      setNotification({
        type: "error",
        message: error.message || "An error occurred while adding manual entry",
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const showSuccessNotification = (amount, date) => {
    const now = new Date();
    setLastSyncTime(
      now.toLocaleString("en-GB", {
 day: "2-digit",
        month: "2-digit",
        year: "numeric",
     hour: "2-digit",
        minute: "2-digit",
      }),
    );
    setNotification({
 type: "success",
      message: `Manual cash entry of ${new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount)} for ${new Date(date).toLocaleDateString("en-GB")} added successfully`,
  });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    // TODO: Open modal or navigate to details page
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
    <>
  <Helmet>
          <title>Daily Takings & TRONC - Rocket Tronc</title>
</Helmet>
        <PageLayout
          title="Daily Takings & TRONC"
          description="Review daily TRONC records and distribution status."
     breadcrumbs={breadcrumbs}
      >
          <div className="card p-12 text-center">
    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
         <Icon name="Building2" size={40} className="text-primary" />
        </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
     <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
     to view their daily takings and TRONC data.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Icon name="ArrowLeft" size={16} />
    <span>Use the tenant selector in the sidebar</span>
     </div>
      </div>
        </PageLayout>
      </>
    );
  }

  const locationCount = locations?.length || 0;

  const getDescription = () => {
    if (!selectedTenant) {
      return "Review daily TRONC records and distribution status.";
    }
    if (currentLocation) {
      return `Viewing ${selectedTenant.name} - ${currentLocation.name}`;
    }
    return `Viewing ${selectedTenant.name} - Company totals across ${locationCount} location${locationCount !== 1 ? "s" : ""}.`;
  };

  return (
    <>
  <Helmet>
        <title>
      Daily Takings & TRONC - {selectedTenant?.name || "Rocket Tronc"}
        </title>
        <meta
  name="description"
  content="Review daily TRONC records and distribution status"
        />
      </Helmet>

      <PageLayout
        title="Daily Takings & TRONC"
        description={getDescription()}
        breadcrumbs={breadcrumbs}
        actions={
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
   <Icon name="RefreshCw" size={16} className={loading ? "animate-spin" : ""} />
            <span className="ml-2">Refresh</span>
          </Button>
        }
      >
        {/* Notification */}
        {notification && (
      <div
      className={`mb-6 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
     <Icon
   name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
   }
              size={20}
       />
            <span>{notification.message}</span>
    <button
      onClick={() => setNotification(null)}
      className="ml-auto p-1 hover:bg-white/20 rounded transition-colors"
  >
         <Icon name="X" size={16} />
            </button>
     </div>
        )}

{/* Loading State */}
        {loading && (
          <div className="card p-8 text-center mb-6">
            <div className="spinner mx-auto mb-4" />
        <p className="text-muted-foreground">Loading data...</p>
          </div>
        )}

     {/* Error State */}
  {error && !loading && (
          <div className="alert alert-warning mb-6">
        <Icon name="AlertTriangle" size={20} />
            <div>
              <p className="alert-title">Unable to load data</p>
<p className="alert-description">{error}</p>
      </div>
       <button
              className="btn btn-outline btn-sm ml-auto"
          onClick={refetch}
            >
  <Icon name="RefreshCw" size={16} />
          Retry
            </button>
   </div>
     )}

    {/* Company Total Banner */}
     {selectedTenant && !currentLocation && locationCount > 1 && (
 <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="Building2" size={20} className="text-primary" />
      <div>
          <p className="font-medium">Viewing Company Totals</p>
           <p className="text-sm text-muted-foreground">
   Showing combined data from all ${locationCount} locations.
      Select a location from the sidebar to view specific location
            data.
                </p>
  </div>
  </div>
          </div>
        )}

        {/* Date Filter */}
        <div className="mb-6 bg-card rounded-xl border border-border p-4">
          <div className="flex flex-wrap items-end gap-4">
         <div className="flex-1 min-w-[200px]">
           <label className="block text-sm font-medium mb-2">
       Start Date
   </label>
      <Input
     type="date"
   value={dateFilter.startDate}
          onChange={(e) =>
        handleDateFilterChange({
...dateFilter,
     startDate: e.target.value,
   })
       }
     max={dateFilter.endDate}
   />
     </div>
            <div className="flex-1 min-w-[200px]">
 <label className="block text-sm font-medium mb-2">End Date</label>
     <Input
   type="date"
     value={dateFilter.endDate}
    onChange={(e) =>
   handleDateFilterChange({
  ...dateFilter,
        endDate: e.target.value,
      })
                }
   min={dateFilter.startDate}
         max={new Date().toISOString().split("T")[0]}
           />
 </div>
       <div className="flex gap-2">
       <Button
           variant="outline"
   size="sm"
       onClick={() => {
    const today = new Date().toISOString().split("T")[0];
        handleDateFilterChange({ startDate: today, endDate: today });
         }}
    >
           Today
  </Button>
            <Button
      variant="outline"
   size="sm"
         onClick={() => {
  const endDate = new Date().toISOString().split("T")[0];
       const startDate = new Date();
     startDate.setDate(startDate.getDate() - 7);
      handleDateFilterChange({
    startDate: startDate.toISOString().split("T")[0],
          endDate,
              });
                }}
              >
  Last 7 Days
      </Button>
      <Button
  variant="outline"
        size="sm"
 onClick={() => {
      const endDate = new Date().toISOString().split("T")[0];
     const startDate = new Date();
       startDate.setDate(startDate.getDate() - 30);
        handleDateFilterChange({
              startDate: startDate.toISOString().split("T")[0],
             endDate,
      });
    }}
              >
    Last 30 Days
      </Button>
       </div>
</div>
      </div>

        {/* Summary Cards */}
   <PageSection>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
    Showing data for:{" "}
    {new Date(dateFilter.startDate).toLocaleDateString("en-GB")}
              {dateFilter.startDate !== dateFilter.endDate &&
 ` - ${new Date(dateFilter.endDate).toLocaleDateString("en-GB")}`}
      </p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
 <span className="badge badge-success">{distributedCount}</span>
             <span className="text-muted-foreground">Distributed</span>
              </div>
              <div className="flex items-center gap-2">
           <span className="badge badge-warning">{pendingCount}</span>
              <span className="text-muted-foreground">Pending</span>
         </div>
          </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
  <TakingsSummaryCard
          title="Cash Tips"
       amount={cashTips}
     icon="Banknote"
    iconColor="var(--color-secondary)"
            />
            <TakingsSummaryCard
    title="Card Gratuities"
              amount={cardTips}
 icon="CreditCard"
    iconColor="var(--color-accent)"
  />
      <TakingsSummaryCard
         title="Service Charge"
          amount={serviceCharge}
 icon="Percent"
   iconColor="var(--color-info)"
            />
 <TakingsSummaryCard
     title="Total TRONC"
    amount={totalTronc}
          icon="PoundSterling"
         iconColor="var(--color-success)"
          />
     </div>
        </PageSection>

        {/* Daily TRONC Records Table */}
        <PageSection 
          title="Daily TRONC Records" 
          description="Review and manage daily TRONC amounts and distribution status"
        >
       <DailyTroncTable 
            records={paginatedRecords} 
         onViewDetails={handleViewDetails}
          />

          {/* Pagination with Export */}
    <Pagination
  page={pagination.page}
         pageSize={pagination.pageSize}
         totalCount={sortedRecords.length}
 totalPages={totalPages}
   onPageChange={handlePageChange}
  onPageSizeChange={handlePageSizeChange}
   onExportCurrentPage={handleExportCurrentPage}
  onExportAll={handleExportAll}
     exportDisabled={sortedRecords.length === 0}
className="mt-4"
          />
        </PageSection>

        {/* Historical Comparison & Manual Entry */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <PageSection title="Historical Comparison">
            <HistoricalComparison
         weekData={preparedHistoricalData.week}
         monthData={preparedHistoricalData.month}
 loading={historicalLoading}
          />
          </PageSection>
          <PageSection title="Manual Cash Entry">
  <ManualEntryPanel onSubmit={handleManualEntry} />
       </PageSection>
        </div>
      </PageLayout>
    </>
  );
};

export default DailyTakingsAndTronc;
